import { motion } from "framer-motion";
import { ArrowDown, MessageSquareText, Phone } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center justify-center overflow-hidden bg-gradient-to-b from-[#f2ecdd] via-[#efe7d2] to-[#e7dcc0]"
    >
      <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full border border-[#1c4431]/10" />
      <div className="pointer-events-none absolute -bottom-52 -left-52 h-[38rem] w-[38rem] rounded-full border border-[#1c4431]/10" />

      <div className="relative z-10 mx-auto w-full max-w-5xl px-4 pb-24 pt-32 text-center sm:px-8">
        <motion.img
          src="/logo.jpg"
          alt="Pratt's Hydroseeding & Excavating logo"
          data-testid="hero-logo"
          initial={{ opacity: 0, scale: 0.85, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto h-36 w-36 rounded-lg object-cover shadow-[0_30px_80px_-30px_rgba(28,68,49,0.5)] ring-1 ring-[#1c4431]/20 sm:h-48 sm:w-48"
        />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-3"
        >
          <span
            data-testid="hero-badge-region"
            className="rounded-full border border-[#1c4431]/25 bg-white/50 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#1f5c3d] backdrop-blur-sm sm:text-xs"
          >
            East Tennessee · Owner Operated
          </span>
          <span className="rounded-full border border-[#1c4431]/25 bg-white/50 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#1f5c3d] backdrop-blur-sm sm:text-xs">
            Licensed & Insured
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="mt-8 font-display text-2xl font-extrabold uppercase leading-[1.08] tracking-tight text-[#1a2e22] sm:text-3xl lg:text-[2.6rem]"
        >
          <MaskedLine delay={0.45}>From land clearing & grading</MaskedLine>
          <MaskedLine delay={0.6}>to hydroseeding, retaining walls,</MaskedLine>
          <MaskedLine delay={0.75}>ponds, pools & erosion control —</MaskedLine>
          <MaskedLine delay={0.95}>
            <span className="text-[#1f5c3d]">we handle the dirt</span>
          </MaskedLine>
          <MaskedLine delay={1.1}>
            <span className="text-[#1f5c3d]">from start to finish.</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subcopy"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="mx-auto mt-8 max-w-xl font-mono text-xs uppercase leading-relaxed tracking-[0.2em] text-[#5c6b58] sm:text-sm"
        >
          Call, text, or email to inquire — pricing happens on site, after we
          walk your property.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <a
            data-testid="hero-call-button"
            href="tel:+14237482012"
            className="group flex items-center gap-3 rounded-full bg-[#1f5c3d] px-8 py-4 font-mono text-sm font-bold uppercase tracking-widest text-[#f6f1e3] shadow-[0_15px_50px_-15px_rgba(28,68,49,0.7)] transition-colors duration-300 hover:bg-[#2a7452]"
          >
            <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
            Call 423-748-2012
          </a>
          <a
            data-testid="hero-text-button"
            href="sms:+14237482012"
            className="flex items-center gap-3 rounded-full border border-[#1c4431]/35 bg-white/40 px-8 py-4 font-mono text-sm font-bold uppercase tracking-widest text-[#1a2e22] backdrop-blur-sm transition-colors duration-300 hover:border-[#1f5c3d] hover:text-[#1f5c3d]"
          >
            <MessageSquareText className="h-4 w-4" />
            Text Us
          </a>
          <button
            data-testid="hero-email-button"
            onClick={() => scrollToId("#contact")}
            className="rounded-full border border-[#1c4431]/35 bg-white/40 px-8 py-4 font-mono text-sm font-bold uppercase tracking-widest text-[#1a2e22] backdrop-blur-sm transition-colors duration-300 hover:border-[#1f5c3d] hover:text-[#1f5c3d]"
          >
            Email Pratt
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.7 }}
        className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#5c6b58]"
      >
        <ArrowDown className="h-4 w-4 animate-bounce text-[#1f5c3d]" />
        Scroll — see what the dirt becomes
      </motion.div>
    </section>
  );
}
